// drawRuleMeta.js — Authoritative map from DrawRule numeric value → metadata.
//
// Source: 2525D/Dch1/E JSDoc descriptions in DrawRules.d.mts, cross-checked against the real
// library's own MSLookup/MSInfo (see the self-check test in __tests__/drawRuleMeta.msinfo.test.js
// and scripts/check-resolve-draw-mode.mjs's --dump-drawrule-minmax mode) — that runtime data is
// the actual source of truth the renderer itself uses to draw a symbol, so minPoints/maxPoints
// below are pinned to what MSInfo.getMinPointCount()/getMaxPointCount() report for a
// representative SIDC of each DrawRule, not hand-derived from the JSDoc prose alone.
//
// Used by: DrawRule badge (TacticalPalette), per-point wizard (PlacementWizard),
//          resolveDrawMode.js (point-count/mode resolution), and every bespoke custom draw
//          interaction.
//
// getDrawRuleMeta(drawRuleNumber) → { name, pointLabels, notes, minPoints, maxPoints } | null
//
//   name        — human-readable constant name, e.g. "LINE12"
//   pointLabels — ordered array; index 0 = P1 description, index 1 = P2, …
//                 Last entry may say "P<n>+: extend" for unlimited rules.
//   notes       — optional short operational note (orientation hints, etc.)
//   minPoints   — MSInfo.getMinPointCount() for a representative SIDC of this DrawRule.
//   maxPoints   — MSInfo.getMaxPointCount() for the same SIDC; Infinity for unlimited rules
//                 (the real library reports Number.MAX_VALUE — normalized to Infinity here so
//                 comparisons/serialization don't have to special-case the sentinel).

import { DrawRules } from '@armyc2.c5isr.renderer/mil-sym-ts-web';

// ponytail: built as a plain array then converted to Map once so callers pay
// one lookup, not an array scan.

// Sentinel for "unlimited" maxPoints — normalized from the real library's
// Number.MAX_VALUE (see module header).
const UNLIMITED = Infinity;

const RAW = [
    // ── DONOTDRAW ────────────────────────────────────────────────────────────
    [DrawRules.DONOTDRAW, {
        name: 'DONOTDRAW',
        pointLabels: [],
        notes: 'Category node — not a placeable graphic.',
        minPoints: 0, maxPoints: 0,
    }],

    // ── AREA family (101–127) ─────────────────────────────────────────────────
    [DrawRules.AREA1, {
        name: 'AREA1',
        pointLabels: ['P1: boundary vertex 1', 'P2: boundary vertex 2', 'P3+: additional boundary vertices'],
        notes: 'Free polygon; add as many points as needed to define boundary.',
        minPoints: 3, maxPoints: UNLIMITED,
    }],
    [DrawRules.AREA2, {
        name: 'AREA2',
        pointLabels: ['P1: boundary vertex 1', 'P2: boundary vertex 2', 'P3+: additional boundary vertices', 'PN+1: LAA point symbol anchor'],
        notes: 'Free polygon + one LAA point connected by a line.',
        minPoints: 3, maxPoints: UNLIMITED,
    }],
    [DrawRules.AREA3, {
        name: 'AREA3',
        pointLabels: ['P1: boundary vertex 1', 'P2: boundary vertex 2', 'P3+: additional boundary vertices'],
        notes: 'Free polygon.',
        minPoints: 3, maxPoints: UNLIMITED,
    }],
    [DrawRules.AREA4, {
        name: 'AREA4',
        pointLabels: ['P1: boundary vertex 1', 'P2: boundary vertex 2', 'P3+: additional boundary vertices'],
        notes: 'Free polygon with tic marks; tic length = echelon field B height.',
        minPoints: 3, maxPoints: UNLIMITED,
    }],
    [DrawRules.AREA5, {
        name: 'AREA5',
        pointLabels: ['P1: semicircle diameter end 1', 'P2: semicircle diameter end 2', 'P3: arrow tip'],
        notes: 'Arrow projects perpendicularly from midpoint of P1–P2 toward P3.',
        minPoints: 3, maxPoints: 3,
    }],
    [DrawRules.AREA6, {
        name: 'AREA6',
        pointLabels: ['P1: center', 'P2: radius start (defines radius and arc-open direction)'],
        notes: '30° arc opening on the friendly side. Radius = distance(P1, P2).',
        minPoints: 2, maxPoints: 2,
    }],
    [DrawRules.AREA7, {
        name: 'AREA7',
        pointLabels: ['P1: base line end 1 (firing position side)', 'P2: base line end 2', 'P3: arrowhead tip (target direction)'],
        notes: 'Arrow projects perpendicularly from midpoint of P1–P2.',
        minPoints: 3, maxPoints: 3,
    }],
    [DrawRules.AREA8, {
        name: 'AREA8',
        pointLabels: ['P1: base line end 1', 'P2: base line end 2', 'P3: arrowhead tip 1', 'P4: arrowhead tip 2'],
        notes: 'Two arrows from base ends P1 and P2 toward P3 and P4 respectively.',
        minPoints: 4, maxPoints: 4,
    }],
    [DrawRules.AREA9, {
        name: 'AREA9',
        pointLabels: ['P1: boundary vertex 1', 'P2: boundary vertex 2', 'P3+: additional boundary vertices'],
        notes: 'Free polygon encompassing fire support symbols.',
        minPoints: 3, maxPoints: UNLIMITED,
    }],
    [DrawRules.AREA10, {
        name: 'AREA10',
        pointLabels: ['P1: boundary vertex 1', 'P2: boundary vertex 2', 'P3: boundary vertex 3', 'P4–P6: optional additional vertices (max 6)'],
        notes: 'Min 3, max 6 anchor points.',
        minPoints: 3, maxPoints: 6,
    }],
    [DrawRules.AREA11, {
        name: 'AREA11',
        pointLabels: ['P1: T-stem end 1', 'P2: T-stem end 2', 'P3: crossbar end'],
        notes: 'Crossbar projects perpendicularly from midpoint of P1–P2. T-head faces enemy.',
        minPoints: 3, maxPoints: 3,
    }],
    [DrawRules.AREA12, {
        name: 'AREA12',
        pointLabels: ['P1: baseline end 1 (height)', 'P2: baseline end 2 (height)', 'P3: arrow length / direction'],
        notes: 'Feather arrows perpendicular to baseline; P3 sets arrow length.',
        minPoints: 3, maxPoints: 3,
    }],
    [DrawRules.AREA13, {
        name: 'AREA13',
        pointLabels: ['P1: corner 1', 'P2: corner 2 (defines straight line + semicircle radius)'],
        notes: 'Semicircle radius = ½ distance(P1, P2).',
        minPoints: 2, maxPoints: 2,
    }],
    [DrawRules.AREA14, {
        name: 'AREA14',
        pointLabels: ['P1: center (Ground Zero)', 'P2: radius of circle 1', 'P3: radius of circle 2'],
        notes: 'Bullseye / nuclear burst. Two concentric circles centered on GZ.',
        minPoints: 3, maxPoints: 3,
    }],
    [DrawRules.AREA15, {
        name: 'AREA15',
        pointLabels: ['P1: center', 'P2: radius start (defines radius and arc-open direction)'],
        notes: '30° arc opening on friendly side (isolate).',
        minPoints: 2, maxPoints: 2,
    }],
    [DrawRules.AREA16, {
        name: 'AREA16',
        pointLabels: ['P1: center', 'P2: radius start (defines radius and arc-open direction)'],
        notes: '30° arc opening on friendly side (occupy).',
        minPoints: 2, maxPoints: 2,
    }],
    [DrawRules.AREA17, {
        name: 'AREA17',
        pointLabels: ['P1: stem end 1', 'P2: stem end 2', 'P3: rear anchor'],
        notes: 'Arrow projects perpendicularly from midpoint of P1–P2 toward enemy.',
        minPoints: 3, maxPoints: 3,
    }],
    [DrawRules.AREA18, {
        name: 'AREA18',
        pointLabels: ['P1: arrow 1 tip', 'P2: arrow 1 rear', 'P3: arrow 2 tip', 'P4: arrow 2 rear'],
        notes: 'Two curved relief arrows. P2–P3 connected by smooth curve.',
        minPoints: 4, maxPoints: 4,
    }],
    [DrawRules.AREA19, {
        name: 'AREA19',
        pointLabels: ['P1: center', 'P2: radius start (defines radius and arc-open direction)'],
        notes: '30° arc opening on friendly side (secure).',
        minPoints: 2, maxPoints: 2,
    }],
    [DrawRules.AREA20, {
        name: 'AREA20',
        pointLabels: ['P1: boundary vertex 1', 'P2: boundary vertex 2', 'P3+: additional boundary vertices'],
        notes: 'Free polygon.',
        minPoints: 3, maxPoints: UNLIMITED,
    }],
    [DrawRules.AREA21, {
        name: 'AREA21',
        pointLabels: ['P1: vertex (center)', 'P2: arrowhead tip 1', 'P3: arrowhead tip 2'],
        notes: 'Two arrows from P1 toward P2 and P3. Arrows can vary independently.',
        minPoints: 3, maxPoints: 3,
    }],
    [DrawRules.AREA22, {
        name: 'AREA22',
        pointLabels: ['P1: center'],
        notes: 'Static single-point symbol.',
        minPoints: 1, maxPoints: 1,
    }],
    [DrawRules.AREA23, {
        name: 'AREA23',
        pointLabels: ['P1: boundary vertex 1', 'P2: boundary vertex 2', 'P3+: additional boundary vertices'],
        notes: 'Free polygon.',
        minPoints: 3, maxPoints: UNLIMITED,
    }],
    [DrawRules.AREA24, {
        name: 'AREA24',
        pointLabels: ['P1: T-stem end 1', 'P2: T-stem end 2', 'P3: crossbar end'],
        notes: 'Horizontal crossbar projects from midpoint of vertical P1–P2. T-head faces enemy.',
        minPoints: 3, maxPoints: 3,
    }],
    [DrawRules.AREA25, {
        name: 'AREA25',
        pointLabels: ['P1: baseline end 1', 'P2: baseline end 2 (height)', 'P3: arrow length direction'],
        notes: 'Parallel feather arrows perpendicular to baseline; point toward enemy.',
        minPoints: 3, maxPoints: 3,
    }],
    [DrawRules.AREA26, {
        name: 'AREA26',
        pointLabels: ['P1–PN/2: inner zone (zone 1) vertices', 'PN/2+1–PN: outer zone (zone 2) vertices'],
        notes: 'Always an even number of points ≥ 6. First half = inner safe zone, second half = outer zone.',
        minPoints: 6, maxPoints: UNLIMITED,
    }],
    // AREA27 has no symbol under SymbolID.Version_2525Dch1 (the version used elsewhere in this
    // app — see resolveDrawMode.js/SymbolRenderer.js) — confirmed empirically by enumerating
    // MSLookup.getIDList() for every SymbolID.Version_* the library exposes; it first appears in
    // 2525E (e.g. SIDC 25152800). minPoints/maxPoints below are pinned to that 2525E example
    // instead, since the DrawRule's point-count semantics don't vary across standard revisions
    // for every other DrawRule checked. See the self-check test for the version-fallback list.
    [DrawRules.AREA27, {
        name: 'AREA27',
        pointLabels: ['P1: arrow tip', 'P2: spine end', 'P3: arc side selector and diameter (distance from P1–P2 line)'],
        notes: '180° arc attached perpendicularly to the spine; P3 picks which side.',
        minPoints: 3, maxPoints: 3,
    }],

    // ── POINT family (201–218) ────────────────────────────────────────────────
    [DrawRules.POINT1, {
        name: 'POINT1',
        pointLabels: ['P1: tip of inverted cone'],
        notes: 'Static single-point symbol.',
        minPoints: 1, maxPoints: 1,
    }],
    [DrawRules.POINT2, {
        name: 'POINT2',
        pointLabels: ['P1: center'],
        notes: 'Static single-point symbol.',
        minPoints: 1, maxPoints: 1,
    }],
    [DrawRules.POINT3, {
        name: 'POINT3',
        pointLabels: ['P1: center'],
        notes: 'Maneuver area static symbol; may be filled.',
        minPoints: 1, maxPoints: 1,
    }],
    [DrawRules.POINT4, {
        name: 'POINT4',
        pointLabels: ['P1: bottom of central vertical line'],
        notes: 'Static single-point symbol.',
        minPoints: 1, maxPoints: 1,
    }],
    [DrawRules.POINT5, {
        name: 'POINT5',
        pointLabels: ['P1: intersection of all lines'],
        notes: 'Static single-point symbol.',
        minPoints: 1, maxPoints: 1,
    }],
    [DrawRules.POINT6, {
        name: 'POINT6',
        pointLabels: ['P1: center of bottom of control measure'],
        notes: 'Static single-point symbol.',
        minPoints: 1, maxPoints: 1,
    }],
    [DrawRules.POINT7, {
        name: 'POINT7',
        pointLabels: ["P1: midpoint of symbol's base"],
        notes: 'Static single-point symbol.',
        minPoints: 1, maxPoints: 1,
    }],
    [DrawRules.POINT8, {
        name: 'POINT8',
        pointLabels: ['P1: center (70° corner angle)'],
        notes: 'User-defined point type.',
        minPoints: 1, maxPoints: 1,
    }],
    [DrawRules.POINT9, {
        name: 'POINT9',
        pointLabels: ['P1: center (height = 2× width)'],
        notes: 'Static single-point symbol.',
        minPoints: 1, maxPoints: 1,
    }],
    [DrawRules.POINT10, {
        name: 'POINT10',
        pointLabels: ['P1: center of circle (sonobuoy)'],
        notes: 'Circle diameter = ½ symbol height.',
        minPoints: 1, maxPoints: 1,
    }],
    [DrawRules.POINT11, {
        name: 'POINT11',
        pointLabels: ['P1: center (length = 2× height)'],
        notes: 'Static single-point symbol.',
        minPoints: 1, maxPoints: 1,
    }],
    [DrawRules.POINT12, {
        name: 'POINT12',
        pointLabels: ['P1: arrowhead tip 1', 'P2: arrowhead tip 2', 'P3: rear anchor'],
        notes: 'Obstacle gap. Opening faces applicable obstacle.',
        minPoints: 3, maxPoints: 3,
    }],
    [DrawRules.POINT13, {
        name: 'POINT13',
        pointLabels: ['P1: center of circle'],
        notes: 'Static single-point symbol.',
        minPoints: 1, maxPoints: 1,
    }],
    [DrawRules.POINT14, {
        name: 'POINT14',
        pointLabels: ['P1: center (45° separation between arrows)'],
        notes: 'Static single-point symbol.',
        minPoints: 1, maxPoints: 1,
    }],
    [DrawRules.POINT15, {
        name: 'POINT15',
        pointLabels: ["P1: nose of symbol"],
        notes: 'Static single-point symbol.',
        minPoints: 1, maxPoints: 1,
    }],
    [DrawRules.POINT16, {
        name: 'POINT16',
        pointLabels: ['P1: circle at base of tower'],
        notes: 'High-angle cone, remains upright.',
        minPoints: 1, maxPoints: 1,
    }],
    [DrawRules.POINT17, {
        name: 'POINT17',
        pointLabels: ['P1: center of rectangle'],
        notes: 'Size from AM1 (length), AM2 (width), rotation angle. 0° = North, clockwise positive.',
        minPoints: 1, maxPoints: 1,
    }],
    [DrawRules.POINT18, {
        name: 'POINT18',
        pointLabels: ['P1: axis of angular rotation'],
        notes: 'Size from AN (search axis azimuth), AM (start/stop range), stop relative bearing.',
        minPoints: 1, maxPoints: 1,
    }],

    // ── LINE family (301–333, 350) ────────────────────────────────────────────
    [DrawRules.LINE1, {
        name: 'LINE1',
        pointLabels: ['P1: start', 'P2: end (P3+: extend line)'],
        notes: 'Simple polyline; min 2 pts.',
        minPoints: 2, maxPoints: UNLIMITED,
    }],
    [DrawRules.LINE2, {
        name: 'LINE2',
        pointLabels: ['P1: start', 'P2: end (P3+: extend line)'],
        notes: 'Polyline with end-of-line info posted at endpoints.',
        minPoints: 2, maxPoints: UNLIMITED,
    }],
    [DrawRules.LINE3, {
        name: 'LINE3',
        pointLabels: ['P1: vertex (fork center)', 'P2: arrowhead tip 1', 'P3: arrowhead tip 2'],
        notes: 'Two independent arrows from P1.',
        minPoints: 3, maxPoints: 3,
    }],
    [DrawRules.LINE4, {
        name: 'LINE4',
        pointLabels: ['P1: corner 1', 'P2: corner 2'],
        notes: 'Varies in length only.',
        minPoints: 2, maxPoints: 2,
    }],
    [DrawRules.LINE5, {
        name: 'LINE5',
        pointLabels: ['P1: bearing origin', 'P2: contact location / direction'],
        notes: 'Bearing line; 2 pts only.',
        minPoints: 2, maxPoints: 2,
    }],
    [DrawRules.LINE6, {
        name: 'LINE6',
        pointLabels: ['P1: vertex (bend point)', 'P2: end 1', 'P3: end 2'],
        notes: 'P1–P2 typically same length as P2–P3.',
        minPoints: 2, maxPoints: UNLIMITED,
    }],
    [DrawRules.LINE7, {
        name: 'LINE7',
        pointLabels: ['P1: start', 'P2: end (P3+: extend line)'],
        notes: 'Polyline with single centered label.',
        minPoints: 2, maxPoints: UNLIMITED,
    }],
    [DrawRules.LINE8, {
        name: 'LINE8',
        pointLabels: ['P1: start', 'P2: end (P3–P300: additional waypoints)'],
        notes: 'Min 2, max 300 pts.',
        minPoints: 2, maxPoints: 300,
    }],
    [DrawRules.LINE9, {
        name: 'LINE9',
        pointLabels: ['P1: arrowhead tip', 'P2: rear of symbol'],
        notes: '2-pt arrow; varies in length only.',
        minPoints: 2, maxPoints: 2,
    }],
    [DrawRules.LINE10, {
        name: 'LINE10',
        pointLabels: ['P1: arrowhead tip', 'P2: rear of symbol', 'P3: arc side selector (distance = arc radius)'],
        notes: 'P1–P2 connected by 90° arc; P3 picks which side the arc bulges.',
        minPoints: 3, maxPoints: 3,
    }],
    [DrawRules.LINE11, {
        name: 'LINE11',
        pointLabels: ['P1: gap side 1 start', 'P2: gap side 1 end', 'P3: gap side 2 start', 'P4: gap side 2 end'],
        notes: '4 pts defining two sides of a gap.',
        minPoints: 4, maxPoints: 4,
    }],
    [DrawRules.LINE12, {
        name: 'LINE12',
        pointLabels: ['P1: centerline start', 'P2: centerline end', 'P3: width side point'],
        notes: 'Width = perpendicular distance from P3 to P1–P2 spine.',
        minPoints: 3, maxPoints: 3,
    }],
    [DrawRules.LINE13, {
        name: 'LINE13',
        pointLabels: ['P1: start', 'P2: end (P3+: extend line)'],
        notes: 'Polyline with fixed-size teeth.',
        minPoints: 2, maxPoints: UNLIMITED,
    }],
    [DrawRules.LINE14, {
        name: 'LINE14',
        pointLabels: ['P1: arrowhead tip 1', 'P2: arrowhead tip 2'],
        notes: 'Double arrowhead; acute angle arrowhead lines.',
        minPoints: 2, maxPoints: 2,
    }],
    // DrawRules.LINE15 is absent from this library version (enum skips 315; LINE14=314, LINE16=316)
    // — confirmed no symbol under ANY SymbolID.Version_* reports getDrawRule()===315, so this
    // entry is genuinely unverifiable against the real library; the self-check test skips it
    // rather than failing on a representative SIDC that can never exist. minPoints/maxPoints
    // below are carried over from the pre-existing pointLabels documentation only.
    // Use the literal 315 so the entry is correctly keyed for any future library version that adds it.
    [315, {
        name: 'LINE15',
        pointLabels: ['P1: arrowhead tip', 'P2: rear of symbol'],
        notes: '2-pt fixed aviation direction-of-attack arrow.',
        minPoints: 2, maxPoints: 2,
        unverifiable: true,
    }],
    [DrawRules.LINE16, {
        name: 'LINE16',
        pointLabels: ['P1: crossing side 1 start', 'P2: crossing side 1 end', 'P3: crossing side 2 start', 'P4: crossing side 2 end'],
        notes: 'Assault crossing site; 4 pts defining two opposing banks.',
        minPoints: 4, maxPoints: 4,
    }],
    [DrawRules.LINE17, {
        name: 'LINE17',
        pointLabels: ['P1: spine start', 'P2: spine end', 'P3: parallel line offset point'],
        notes: 'P3 distance from spine determines separation between parallel lines.',
        minPoints: 3, maxPoints: 3,
    }],
    [DrawRules.LINE18, {
        name: 'LINE18',
        pointLabels: ['P1: arrowhead tip 1', 'P2: arrowhead tip 2'],
        notes: 'Filled double arrowhead.',
        minPoints: 2, maxPoints: 2,
    }],
    [DrawRules.LINE19, {
        name: 'LINE19',
        pointLabels: ['P1: front corner 1', 'P2: front corner 2'],
        notes: 'Varies in length only.',
        minPoints: 2, maxPoints: 2,
    }],
    [DrawRules.LINE20, {
        name: 'LINE20',
        pointLabels: ['P1: arrowhead tip (convoy halted location)', 'P2: rear of graphic'],
        notes: 'Arrow points to halted convoy location.',
        minPoints: 2, maxPoints: 2,
    }],
    [DrawRules.LINE21, {
        name: 'LINE21',
        pointLabels: ['P1: start', 'P2: end (P3+: extend line)'],
        notes: 'Repeating segment pattern; each segment re-labels.',
        minPoints: 2, maxPoints: UNLIMITED,
    }],
    [DrawRules.LINE22, {
        name: 'LINE22',
        pointLabels: ['P1: breach opening end 1', 'P2: breach opening end 2', 'P3: rear anchor (breach depth)'],
        notes: 'Opening faces enemy. Rear line = same height as opening.',
        minPoints: 3, maxPoints: 3,
    }],
    [DrawRules.LINE23, {
        name: 'LINE23',
        pointLabels: ['P1: vertical line end 1', 'P2: vertical line end 2', 'P3: rear anchor'],
        notes: 'Three arrows perpendicular to vertical line; middle tip at midpoint.',
        minPoints: 3, maxPoints: 3,
    }],
    [DrawRules.LINE24, {
        name: 'LINE24',
        pointLabels: ['P1: arrowhead tip', 'P2: spine end', 'P3: arc side + diameter (P3 side = arc side)'],
        notes: '180° arc perpendicular to spine. Unit current location at base of arc.',
        minPoints: 3, maxPoints: 3,
    }],
    [DrawRules.LINE25, {
        name: 'LINE25',
        pointLabels: ['P1: arrowhead tip', 'P2: rear of symbol'],
        notes: 'Exactly 2 pts; direction of action.',
        minPoints: 2, maxPoints: 2,
    }],
    [DrawRules.LINE26, {
        name: 'LINE26',
        pointLabels: [
            '3-pt form — P1: vertex, P2: arrowhead tip 1, P3: arrowhead tip 2',
            '4-pt form — P1: arrow 1 tip, P2: arrow 1 rear, P3: arrow 2 tip, P4: arrow 2 rear',
        ],
        notes: 'Double-click after 3 pts to use 3-pt form; place 4th pt for independent arrows.',
        minPoints: 3, maxPoints: 4,
    }],
    [DrawRules.LINE27, {
        name: 'LINE27',
        pointLabels: [
            '3-pt form — P1: circle center, P2: arc point, P3: arc side',
            '4-pt form — P1: circle center, P2: circle radius, P3: arc curvature, P4: arrow end',
        ],
        notes: 'Circle contains unit symbol. Arrow indicates objective.',
        minPoints: 3, maxPoints: 4,
    }],
    [DrawRules.LINE28, {
        name: 'LINE28',
        pointLabels: ['P1: arrowhead tip', 'P2: rear of symbol'],
        notes: 'Duplicate of LINE9 (removed in 2525E); 2-pt arrow.',
        minPoints: 2, maxPoints: 2,
    }],
    [DrawRules.LINE29, {
        name: 'LINE29',
        pointLabels: ['P1: arrowhead tip', 'P2: curved-back end 1', 'P3: curved-back end 2'],
        notes: 'Ambush arrow. Curved back faces ambush position; arrowhead points at target.',
        minPoints: 3, maxPoints: 3,
    }],
    // LINE30–33 and LINE50 have no symbol under SymbolID.Version_2525Dch1 (this app's version —
    // see AREA27's comment above); confirmed present under Version_2525D/APP6D instead (same
    // point-count values also hold under 2525E/APP6E variants that carry them).
    [DrawRules.LINE30, {
        name: 'LINE30',
        pointLabels: ['P1: arrowhead tip', 'P2: spine end', 'P3: tail length control (45° angled lines)'],
        notes: 'Dashed arrow with angled tail.',
        minPoints: 3, maxPoints: 3,
    }],
    [DrawRules.LINE31, {
        name: 'LINE31',
        pointLabels: ['P1: spine start', 'P2: spine end', 'P3: arc diameter', 'P4: arc side selector'],
        notes: '180° arc attached to end of spine. P3 diameter; P4 picks side.',
        minPoints: 4, maxPoints: 4,
    }],
    [DrawRules.LINE32, {
        name: 'LINE32',
        pointLabels: ['P1: S-curve arrow end', 'P2: S-curve pivot center', 'P3: S-curve far end (arrowhead)'],
        notes: 'S-curve formed by two 90° arcs.',
        minPoints: 3, maxPoints: 3,
    }],
    [DrawRules.LINE33, {
        name: 'LINE33',
        pointLabels: ['P1: spine start', 'P2: spine end', 'P3: arc side + diameter'],
        notes: '180° arc on one side; P3 picks side and sets diameter.',
        minPoints: 3, maxPoints: 3,
    }],
    // LINE50 — escort placeholder (value is hardcoded in DrawRules as 350)
    [350, {
        name: 'LINE50',
        pointLabels: ['P1: width control (slides main line)', 'P2: main line end 1', 'P3: main line end 2'],
        notes: 'Escort placeholder — draw rule not yet defined by MilStd/APP6.',
        minPoints: 3, maxPoints: 3,
    }],

    // ── CORRIDOR family (401–) ────────────────────────────────────────────────
    [DrawRules.CORRIDOR1, {
        name: 'CORRIDOR1',
        pointLabels: ['P1: ACP/CCP segment start', 'P2: segment end (P3+: additional waypoints, max 99)'],
        notes: 'Multi-segment route; each segment box labels independently. Width controlled by AM modifier (half-width in metres; default 500 m, editable post-placement).',
        minPoints: 2, maxPoints: UNLIMITED,
    }],

    // ── AXIS family (501–502) ─────────────────────────────────────────────────
    [DrawRules.AXIS1, {
        name: 'AXIS1',
        pointLabels: ['P1: arrowhead tip', 'P2–P(N-1): spine waypoints', 'PN: width point (last click; locks chevron width)'],
        notes: 'N between 3 and 50. Last point defines axis width, not spine direction.',
        minPoints: 3, maxPoints: 50,
    }],
    [DrawRules.AXIS2, {
        name: 'AXIS2',
        pointLabels: ['P1: arrowhead tip', 'P2–P(N-1): spine waypoints', 'PN: width point (last click)'],
        notes: 'N between 3 and 50. Identical gesture to AXIS1.',
        minPoints: 3, maxPoints: 50,
    }],

    // ── POLYLINE family (601–) ────────────────────────────────────────────────
    [DrawRules.POLYLINE1, {
        name: 'POLYLINE1',
        pointLabels: ['P1: lane centerline start', 'P2: lane centerline end', 'P3: width side point'],
        notes: 'Infiltration lane. Width = perpendicular distance from P3 to P1–P2.',
        minPoints: 3, maxPoints: 3,
    }],

    // ── ELLIPSE family (701–) ─────────────────────────────────────────────────
    [DrawRules.ELLIPSE1, {
        name: 'ELLIPSE1',
        pointLabels: ['P1: ellipse center'],
        notes: 'Shape from AM (major/minor radius) and rotation angle in properties panel.',
        minPoints: 1, maxPoints: 1,
    }],

    // ── RECTANGULAR family (801–803) ─────────────────────────────────────────
    [DrawRules.RECTANGULAR1, {
        name: 'RECTANGULAR1',
        pointLabels: ['P1: center of side 1', 'P2: center of opposite side 2'],
        notes: 'P1–P2 defines length. Width set via AM modifier in properties panel.',
        minPoints: 2, maxPoints: 2,
    }],
    [DrawRules.RECTANGULAR2, {
        name: 'RECTANGULAR2',
        pointLabels: ['P1: center of rectangle'],
        notes: 'Size from AM (target length, target width) and attitude (mils) in properties panel.',
        minPoints: 1, maxPoints: 1,
    }],
    [DrawRules.RECTANGULAR3, {
        name: 'RECTANGULAR3',
        pointLabels: ['P1: center'],
        notes: 'Center point + AM width; target symbol always upright at center.',
        minPoints: 2, maxPoints: 2,
    }],

    // ── CIRCULAR family (901–902) ─────────────────────────────────────────────
    [DrawRules.CIRCULAR1, {
        name: 'CIRCULAR1',
        pointLabels: ['P1: center', 'P2: any point on circumference (defines radius)'],
        notes: '2-click circle placement.',
        minPoints: 1, maxPoints: 1,
    }],
    [DrawRules.CIRCULAR2, {
        name: 'CIRCULAR2',
        pointLabels: ['P1: object location (center)'],
        notes: 'Range fan. Radii (1–3 concentric rings) set via AM modifier in properties panel.',
        minPoints: 1, maxPoints: 1,
    }],

    // ── ARC family (1001) ─────────────────────────────────────────────────────
    [DrawRules.ARC1, {
        name: 'ARC1',
        pointLabels: ['P1: axis of angular rotation (weapon/sensor location)'],
        notes: 'Sector fan. AN = search axis azimuth; AM = start/stop range. All set in properties panel.',
        minPoints: 1, maxPoints: 1,
    }],
];

// Build lookup map once at module load
const META_MAP = new Map(RAW);

/**
 * @param {number} drawRule — numeric DrawRule value from MSInfo.getDrawRule()
 * @returns {{ name: string, pointLabels: string[], notes: string, minPoints: number, maxPoints: number } | null}
 */
export function getDrawRuleMeta(drawRule) {
    return META_MAP.get(drawRule) ?? null;
}

/**
 * Enumerates every DrawRule key this table declares metadata for — used by
 * scripts/check-resolve-draw-mode.mjs's cross-check against the real library's MSInfo, and by
 * any other caller that needs to walk the full table rather than look up one DrawRule.
 * @returns {number[]}
 */
export function getAllDrawRuleMetaKeys() {
    return [...META_MAP.keys()];
}

// Self-check: warn in dev if this table is missing an entry for any DrawRule the real library
// currently defines. Enumerates DrawRules' own keys (values, since several names literally
// alias the same numeric value — e.g. Version_APP6D === Version_2525D=10 in SymbolID, though
// DrawRules itself doesn't have that pattern today) instead of a hand-maintained magic-number
// EXPECTED_COUNT — a future library version adding/removing a DrawRule constant is caught here
// automatically rather than requiring a manual count bump.
//
// Known, intentional gaps (not flagged):
//   - LINE15 (315): absent from DrawRules itself (enum skips 315) but present here under the
//     literal key for forward-compatibility — see LINE15's own comment above.
if (process.env.NODE_ENV !== 'production') {
    const missing = Object.entries(DrawRules)
        .filter(([, value]) => !META_MAP.has(value))
        .map(([name, value]) => `${name}=${value}`);
    console.assert(
        missing.length === 0,
        `[drawRuleMeta] Table is missing metadata for DrawRule(s): ${missing.join(', ')}. ` +
        `Add an entry to RAW in drawRuleMeta.js for each.`,
    );
}

